import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC } from "react";
import { css } from "styled-components";

/* Custom Code - Start */
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { historyTableProps } from "@store/Themes/common/props/history";
import { tableGetEllipsisCellProps } from "@store/Themes/common/props/table";
/* Custom Code - End */

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
}

type Props = OwnProps;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    // Custom Code
    quoteNumberCell?: DataTableCellProps;
    typeCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    requestedDateCell?: DataTableCellProps;
    expirationDateCell?: DataTableCellProps;
    salesRepCell?: DataTableCellProps;
    userCell?: DataTableCellProps;
    customerCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteNumberCell: historyTableProps.orderNumberCell,
    typeCell: tableGetEllipsisCellProps(60),
    requestedDateCell: historyTableProps.dateCell,
    expirationDateCell: historyTableProps.dateCell,
    statusCell: historyTableProps.statusCell,
    salesRepCell: tableGetEllipsisCellProps(100),
    userCell: tableGetEllipsisCellProps(100),
    customerCell: tableGetEllipsisCellProps(135),
    shipToCell: historyTableProps.shipToCell,
};

const QuoteSummaryCard: FC<Props> = ({ quote, session, quoteSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }
    const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;

    return (
        /* Custom Code - Start */
        <DataTableRow data-test-selector="recentQuotes">
            <DataTableCell {...styles.quoteNumberCell}>
                <RfqQuoteDetailsPageTypeLink title={quote.quoteNumber} quoteId={quote.id} />
            </DataTableCell>
            {quoteSettings.jobQuoteEnabled && <DataTableCell {...styles.typeCell}>{quote.typeDisplay}</DataTableCell>}
            <DataTableCell {...styles.statusCell}>{quote.statusDisplay}</DataTableCell>
            <DataTableCell {...styles.requestedDateCell}>{orderDateDisplay}</DataTableCell>
            <DataTableCell {...styles.expirationDateCell}>{expirationDateDisplay}</DataTableCell>
            {session.isSalesPerson && (
                <>
                    <DataTableCell {...styles.salesRepCell}>{quote.salespersonName}</DataTableCell>
                    <DataTableCell {...styles.userCell}>{quote.userName}</DataTableCell>
                </>
            )}
            <DataTableCell {...styles.customerCell}>{customerDisplay}</DataTableCell>
            {!session.isSalesPerson && <DataTableCell {...styles.shipToCell}>{quote.shipToFullAddress}</DataTableCell>}
        </DataTableRow>
        /* Custom Code - End */
    );
};

export default QuoteSummaryCard;
